import { getCTAByPlaceholder, getCTAText, getCTAUrl } from '../../config/SiteConfigManager';
import { logger } from '../core/logger';

/**
 * Generic CTA replacement utility
 * Replaces placeholders in content with actual CTA buttons/links
 */

// ============================================================================
// CTA URL Override Storage with Subscription Support
// ============================================================================
// Store current CTA URL overrides from API response (keyed by cta_id)
// This enables dynamic CTA URLs based on form data collected during conversation
let currentCTAOverrides: Record<string, string> | null = null;
let ctaOverridesVersion = 0;
const subscribers = new Set<() => void>();

/**
 * Subscribe to CTA override changes
 * @param callback - Function to call when overrides change
 * @returns Unsubscribe function
 */
export function subscribeToCTAOverrides(callback: () => void): () => void {
    subscribers.add(callback);
    return () => subscribers.delete(callback);
}

/**
 * Get the current CTA overrides version number
 * Increments each time overrides are set
 */
export function getCTAOverridesVersion(): number {
    return ctaOverridesVersion;
}

/**
 * Set CTA URL overrides from the backend API response
 * Called after receiving the 'complete' event from streaming
 * @param overrides - Map of cta_id to override URL, or null to clear
 */
export function setCTAOverrides(overrides: Record<string, string> | null): void {
    currentCTAOverrides = overrides;
    if (overrides) {
        ctaOverridesVersion++;
        logger.debug('CTA URL overrides set:', overrides, 'version:', ctaOverridesVersion);
        // Notify all subscribers
        subscribers.forEach((callback) => {
            callback();
        });
    }
}

/**
 * Replace CTA placeholders in text content with actual URLs
 * @param domain - The domain to get CTA configuration for
 * @param content - The content with placeholders like <<DEMO-BUTTON>> or <<PLACEHOLDER-SKELLO-CTA2>>
 * @param language - Language for the CTA text/URL
 * @returns Content with placeholders replaced by URLs
 */
export function replaceCTAPlaceholdersWithUrls(
    domain: string,
    content: string,
    language: string = 'en'
): string {
    // Find all placeholders in the format <<...>>
    const placeholderRegex = /<<([^<>]+)>>/g;

    return content.replace(placeholderRegex, (match, placeholder) => {
        const fullPlaceholder = `<<${placeholder}>>`;
        const url = getCTAUrl(domain, fullPlaceholder, language);
        return url || match; // Return original if no URL found
    });
}

/**
 * Replace CTA placeholders in text content with actual button text
 * @param domain - The domain to get CTA configuration for
 * @param content - The content with placeholders like <<DEMO-BUTTON>> or <<PLACEHOLDER-SKELLO-CTA2>>
 * @param language - Language for the CTA text
 * @returns Content with placeholders replaced by button text
 */
export function replaceCTAPlaceholdersWithText(
    domain: string,
    content: string,
    language: string = 'en'
): string {
    // Find all placeholders in the format <<...>>
    const placeholderRegex = /<<([^<>]+)>>/g;

    return content.replace(placeholderRegex, (match, placeholder) => {
        const fullPlaceholder = `<<${placeholder}>>`;
        const text = getCTAText(domain, fullPlaceholder, language);
        return text || match; // Return original if no text found
    });
}

/**
 * Get all CTA placeholders found in content
 * @param content - The content to scan for placeholders
 * @returns Array of placeholder strings found
 */
export function extractCTAPlaceholders(content: string): string[] {
    // Match placeholders that don't contain << inside them (to avoid malformed ones)
    const placeholderRegex = /<<([^<>]+)>>/g;
    const placeholders: string[] = [];
    let match: RegExpExecArray | null;

    match = placeholderRegex.exec(content);
    while (match !== null) {
        const fullPlaceholder = `<<${match[1]}>>`;
        if (!placeholders.includes(fullPlaceholder)) {
            placeholders.push(fullPlaceholder);
        }
        match = placeholderRegex.exec(content);
    }

    return placeholders;
}

/**
 * Check if content contains any CTA placeholders
 * @param content - The content to check
 * @returns True if content contains CTA placeholders
 */
export function hasCTAPlaceholders(content: string): boolean {
    const placeholderRegex = /<<([^<>]+)>>/g;
    return placeholderRegex.test(content);
}

/**
 * Get CTA data for a specific placeholder
 * Supports dynamic URL overrides from form data collection during conversation
 * @param domain - The domain to get CTA configuration for
 * @param placeholder - The placeholder (e.g., "<<DEMO-BUTTON>>")
 * @param language - Language for the CTA
 * @returns Object with url, text, and optional ctaId, or undefined if not found
 */
export function getCTAData(
    domain: string,
    placeholder: string,
    language: string = 'en'
): { url: string; text: string; ctaId?: string } | undefined {
    const cta = getCTAByPlaceholder(domain, placeholder);
    const staticUrl = getCTAUrl(domain, placeholder, language);
    const text = getCTAText(domain, placeholder, language);

    logger.debug('getCTAData called:', {
        domain,
        placeholder,
        ctaId: cta?.cta_id,
        staticUrl,
        hasOverrides: !!currentCTAOverrides,
        overrides: currentCTAOverrides,
    });

    if (!staticUrl || !text) {
        logger.debug('getCTAData: no staticUrl or text, returning undefined');
        return undefined;
    }

    // Check for dynamic URL override (keyed by cta_id)
    // Override URLs are set by the backend based on form data collected during conversation
    const ctaId = cta?.cta_id;
    let url = staticUrl;

    if (ctaId && currentCTAOverrides?.[ctaId]) {
        url = currentCTAOverrides[ctaId];
        logger.debug('Using CTA URL override:', {
            ctaId,
            originalUrl: staticUrl,
            overrideUrl: url,
        });
    } else {
        logger.debug('No CTA URL override found:', { ctaId, hasOverrides: !!currentCTAOverrides });
    }

    return {
        url,
        text,
        ctaId,
    };
}

/**
 * Get all available CTA data for all placeholders in content
 * @param domain - The domain to get CTA configuration for
 * @param content - The content to scan
 * @param language - Language for the CTAs
 * @returns Map of placeholder to CTA data
 */
export function getAllCTAData(
    domain: string,
    content: string,
    language: string = 'en'
): Record<string, { url: string; text: string; ctaId?: string }> {
    const placeholders = extractCTAPlaceholders(content);
    const ctaData: Record<string, { url: string; text: string; ctaId?: string }> = {};

    placeholders.forEach((placeholder) => {
        const data = getCTAData(domain, placeholder, language);
        if (data) {
            ctaData[placeholder] = data;
        }
    });

    return ctaData;
}
